import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../../services/navigation/navigation.service';
import { CacheService } from '../../services/cache/cache.service';
import { CommonService } from '../../services/common/common.service';
import { Column } from '../interfaces/column.interface';
import moment from 'moment';
import { FitnessClassesDataService } from './fitness-classes-data.service';
import { FitnessClass, FitnessClassesResult } from '../interfaces';
import { CoachesDataService } from './coaches-data.service';
import { Coach, CoachesResult } from '../interfaces';
import { UsersDataService } from './users-data.service';
import { User, UsersResult } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class FitnessSessionsColumnsService {

  public fitnessClasses: FitnessClass[] = [];
  public coaches: Coach[] = [];
  public users: User[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private cache: CacheService,
    private common: CommonService,
    private fitnessClassesDataService: FitnessClassesDataService,
    private coachesDataService: CoachesDataService,
    private usersDataService: UsersDataService,

  ) {
    this.getFitnessClasses()
    this.getCoaches()
    this.getUsers()
  }

  getFitnessClasses(offset: number = 0): void {
    const cacheKey: string = 'fitnessClasses.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.fitnessClasses = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.fitnessClassesDataService.getFitnessClasses(parameters).subscribe((response: FitnessClassesResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.fitnessClasses = response.results;
        } else {
          this.fitnessClasses = this.fitnessClasses.concat(response.results);
        }
        if (response.inlineCount > this.fitnessClasses.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getFitnessClasses(offset);
        }
        response.results = this.fitnessClasses;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getCoaches(offset: number = 0): void {
    const cacheKey: string = 'coaches.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.coaches = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.coachesDataService.getCoaches(parameters).subscribe((response: CoachesResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.coaches = response.results;
        } else {
          this.coaches = this.coaches.concat(response.results);
        }
        if (response.inlineCount > this.coaches.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getCoaches(offset);
        }
        response.results = this.coaches;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getUsers(offset: number = 0): void {
    const cacheKey: string = 'users.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.users = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.usersDataService.getUsers(parameters).subscribe((response: UsersResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.users = response.results;
        } else {
          this.users = this.users.concat(response.results);
        }
        if (response.inlineCount > this.users.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getUsers(offset);
        }
        response.results = this.users;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getColumns(): Column[] {
    return [
      { field: 'id', sortField: 'id', title: this.translate.instant('ID'), show: (this.navigation.getParamValue('fitnessSessions.id.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'name', sortField: 'name', title: this.translate.instant('NAME'), show: (this.navigation.getParamValue('fitnessSessions.name.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'nameAr', sortField: 'nameAr', title: this.translate.instant('NAMEAR'), show: (this.navigation.getParamValue('fitnessSessions.nameAr.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'nameFr', sortField: 'nameFr', title: this.translate.instant('NAMEFR'), show: (this.navigation.getParamValue('fitnessSessions.nameFr.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'startTime', sortField: 'startTime', title: this.translate.instant('STARTTIME'), show: (this.navigation.getParamValue('fitnessSessions.startTime.showFiled', true) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'fitnessClass', title: this.translate.instant('FITNESSCLASS'), type: 'linkValue', filterData: this.fitnessClasses, show: (this.navigation.getParamValue('fitnessSessions.fitnessClassId.showFiled', true) && true), displayInList: true, filterField: 'fitnessClass.id', displayField: 'name', sortField: 'fitnessClass.name', url: 'fitness-classes' },
      { field: 'coach', title: this.translate.instant('COACH'), type: 'linkValue', filterData: this.coaches, show: (this.navigation.getParamValue('fitnessSessions.coachId.showFiled', true) && true), displayInList: true, filterField: 'coach.id', displayField: 'name', sortField: 'coach.name', url: 'coaches' },
      { field: 'pushStreamUrl', sortField: 'pushStreamUrl', title: this.translate.instant('PUSHSTREAMURL'), show: (this.navigation.getParamValue('fitnessSessions.pushStreamUrl.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'pushStreamName', sortField: 'pushStreamName', title: this.translate.instant('PUSHSTREAMNAME'), show: (this.navigation.getParamValue('fitnessSessions.pushStreamName.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'publicStreamUrl', sortField: 'publicStreamUrl', title: this.translate.instant('PUBLICSTREAMURL'), show: (this.navigation.getParamValue('fitnessSessions.publicStreamUrl.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'vodUrl', sortField: 'vodUrl', title: this.translate.instant('VODURL'), show: (this.navigation.getParamValue('fitnessSessions.vodUrl.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'recordingEnabled', sortField: 'recordingEnabled', title: this.translate.instant('RECORDINGENABLED'), show: (this.navigation.getParamValue('fitnessSessions.recordingEnabled.showFiled', false) && true), displayInList: true, type: 'booleanValue', filterData: this.navigation.booleanOptions },
      { field: 'isPublished', sortField: 'isPublished', title: this.translate.instant('ISPUBLISHED'), show: (this.navigation.getParamValue('fitnessSessions.isPublished.showFiled', false) && true), displayInList: true, type: 'booleanValue', filterData: this.navigation.booleanOptions },
      { field: 'createdAt', sortField: 'createdAt', title: this.translate.instant('CREATEDAT'), show: (this.navigation.getParamValue('fitnessSessions.createdAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'creatorUser', title: this.translate.instant('CREATORUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('fitnessSessions.creatorUser.showFiled', false) && true), displayInList: true, filterField: 'creatorUser.id', displayField: 'username', sortField: 'creatorUser.username', url: 'users' },
      { field: 'modifiedAt', sortField: 'modifiedAt', title: this.translate.instant('MODIFIEDAT'), show: (this.navigation.getParamValue('fitnessSessions.modifiedAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'modifierUser', title: this.translate.instant('MODIFIERUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('fitnessSessions.modifierUser.showFiled', false) && true), displayInList: true, filterField: 'modifierUser.id', displayField: 'username', sortField: 'modifierUser.username', url: 'users' },
      { field: 'actions', title: this.translate.instant('ACTIONS'), type: 'static', show: true, displayInList: true, sortField: null},
    ];
  }

  format(fieldName: string, value: any) {
    if (fieldName.toLowerCase().indexOf('time') > -1) {
      return moment(value).format('YYYY-MM-DD HH:mm');
    } else if (fieldName.toLowerCase().indexOf('date') > -1) {
      return moment(value).format('YYYY-MM-DD');
    }
    return value;
  }

}
